import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Download, Upload, Loader2 } from 'lucide-react';

const DatabaseBackupRestore: React.FC = () => {
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  const handleBackup = async () => {
    try {
      setIsBackingUp(true);
      setMessage(null);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/database/backup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || '备份失败');
      }
      
      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get the filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'database_backup.sql.gz'; // Default filename with correct extension
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      // Ensure the filename has the .gz extension
      if (!filename.endsWith('.gz')) {
        filename += '.gz';
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setMessage({
        type: 'success',
        text: '数据库备份成功，文件已下载'
      });
    } catch (error) {
      console.error('Error backing up database:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '备份过程中发生错误'
      });
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) {
      setMessage({
        type: 'error',
        text: '请先选择备份文件'
      });
      return;
    }

    try {
      setIsRestoring(true);
      setMessage(null);
      
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/database/restore', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || '恢复失败');
      }
      
      const result = await response.json();
      setMessage({
        type: 'success',
        text: result.message || '数据库恢复成功'
      });
      
      setIsRestoreDialogOpen(false);
      setSelectedFile(null);
    } catch (error) {
      console.error('Error restoring database:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '恢复过程中发生错误'
      });
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>数据库备份与恢复</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {message && (
            <div className={`p-4 rounded ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {message.text}
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleBackup}
              disabled={isBackingUp}
              className="bg-blue-500 hover:bg-blue-600 flex items-center"
            >
              {isBackingUp ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              备份数据库
            </Button>
            
            <Button
              onClick={() => setIsRestoreDialogOpen(true)}
              className="bg-amber-500 hover:bg-amber-600 flex items-center"
            >
              <Upload className="w-4 h-4 mr-2" />
              恢复数据库
            </Button>
          </div>
          
          <div className="text-sm text-gray-500 mt-4">
            <p>说明：</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>备份功能将下载数据库的完整备份文件</li>
              <li>恢复功能将使用备份文件覆盖当前数据库中的数据</li>
              <li>恢复操作不可撤销，请确保已备份重要数据</li>
            </ul>
          </div>
        </div>
      </CardContent>
      
      {/* Restore Dialog */}
      <Dialog open={isRestoreDialogOpen} onOpenChange={setIsRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>恢复数据库</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-amber-600 font-semibold mb-4">警告：此操作将覆盖当前数据库中的所有数据，不可撤销！</p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                选择备份文件
              </label>
              <input
                type="file"
                accept=".sql,.gz,.sql.gz"
                onChange={handleFileChange}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            
            {selectedFile && (
              <p className="text-sm text-gray-500">
                已选择：{selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                {selectedFile.name.endsWith('.gz') && ' (压缩文件)'}
              </p>
            )}
          </div>
          <DialogFooter className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsRestoreDialogOpen(false)}
            >
              取消
            </Button>
            <Button
              type="button"
              onClick={handleRestore}
              disabled={!selectedFile || isRestoring}
              className="bg-amber-500 hover:bg-amber-600 flex items-center"
            >
              {isRestoring ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              确认恢复
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DatabaseBackupRestore; 